import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { 
  CssBaseline, 
  Box, 
  Typography, 
  IconButton, 
  Slider, 
  Stack, 
  Paper, 
  Tooltip, 
  Fade,
  Alert,
  Snackbar,
  ClickAwayListener
} from '@mui/material';

// --- Icons for Music Player ---
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';

// --- Context Imports ---
import LanguageProvider from './context/LanguageContext';
import CurrencyProvider from './context/CurrencyContext'; 

import { HelmetProvider } from 'react-helmet-async';

// --- Component Imports ---
import LoadingScreen from './components/LoadingScreen';
import NaBar from './components/NavBar';
import Compass from './components/CompassBar';
import ScrollActions from './components/ScrollActions';
import HomePage from './LandingPage/index';
import AboutPage from './pages/Blogs';
import AboutUs from './AboutUs/index';
import ContactPage from './pages/ContactPage';
import Experiences from './Experiences/index';
import Destination from './Destinations/index';
import DestinationDetail from './pages/DestinationDetail';
import Trips from './pages/Trips';
import TripDetail from './pages/TripDetail';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import TripPlannerLanding from './pages/TripPlannerLanding';
import CustomizeTourWizard from './components/planner/CustomizeTourWizard';
import BuildTripWizard from './components/planner/BuildTripWizard';
import ConsultationForm from './components/planner/ConsultationForm';
import 'leaflet/dist/leaflet.css';

// --- ROBUST & ELEGANT MUSIC PLAYER COMPONENT ---
const MiniVisualizer = () => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px', height: 24, width: 24 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: '4px',
          backgroundColor: '#B9935A',
          borderRadius: '2px',
          animation: `equalize ${0.5 + i * 0.2}s infinite alternate ease-in-out`,
          height: '4px' // Base height
        }}
      />
    ))}
  </Box>
);

const BackgroundMusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const [isExpanded, setIsExpanded] = useState(false);
  const[errorMsg, setErrorMsg] = useState('');
  
  const audioRef = useRef(null);

  const getMusicPath = () => {
    return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/music/bgm.mp3` : '/music/bgm.mp3';
  };

  // Autoplay with fallback to first user interaction
  const handleInteraction = async () => {
    removeInteractionListeners();
    if (audioRef.current && !isPlaying) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setErrorMsg('');
      } catch (e) {
        console.log("Play on interact failed:", e);
      }
    }
  };

  const removeInteractionListeners = () => {
    ['click', 'scroll', 'touchstart', 'keydown'].forEach(event =>
      document.removeEventListener(event, handleInteraction)
    );
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
      // Try Auto-Play immediately
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // If blocked by browser policy, wait for ANY interaction on the site to start playing smoothly
        ['click', 'scroll', 'touchstart', 'keydown'].forEach(event =>
          document.addEventListener(event, handleInteraction, { once: true })
        );
      });
    }
    return () => removeInteractionListeners();
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = (e) => {
    e.stopPropagation();
    removeInteractionListeners(); // Prevent interaction listener if manual toggle used
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setErrorMsg('');
        })
        .catch((error) => {
          console.error("Playback Error:", error);
          setIsPlaying(false);
          if (error.name === 'NotAllowedError') {
            setErrorMsg("Click the button again to start music (Browser Policy)");
          } else {
            setErrorMsg("Could not play audio file.");
          }
        });
    }
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  const renderIcon = () => {
    if (isPlaying) {
      // Show pause button when expanded for control, otherwise show the bouncing equalizer 
      return isExpanded ? <PauseIcon sx={{ color: '#B9935A' }} /> : <MiniVisualizer />;
    }
    // Static note when paused
    return <MusicNoteIcon sx={{ color: '#B9935A' }} />;
  };

  return (
    <>
      <style>
        {`
          @keyframes equalize {
            0% { height: 4px; }
            100% { height: 18px; }
          }
          @keyframes pulseGlow {
            0% { box-shadow: 0 0 0 0 rgba(185, 147, 90, 0.4); }
            70% { box-shadow: 0 0 0 12px rgba(185, 147, 90, 0); }
            100% { box-shadow: 0 0 0 0 rgba(185, 147, 90, 0); }
          }
        `}
      </style>

      <audio 
        ref={audioRef} 
        loop 
        onError={() => {
          setErrorMsg("Error: Audio file not found at /music/bgm.mp3");
          setIsPlaying(false);
        }}
        preload="auto"
      >
        <source src={getMusicPath()} type="audio/mpeg" />
      </audio>

      <Snackbar 
        open={!!errorMsg} 
        autoHideDuration={6000} 
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <ClickAwayListener onClickAway={() => setIsExpanded(false)}>
        <Paper
          elevation={isExpanded ? 8 : 4}
          onMouseEnter={() => setIsExpanded(true)}
          onMouseLeave={() => setIsExpanded(false)}
          onClick={() => !isExpanded && setIsExpanded(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 9999,
            borderRadius: '50px',
            padding: isExpanded ? '8px 16px 8px 8px' : '8px',
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: isExpanded ? 1.5 : 0,
            transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
            maxWidth: isExpanded ? '280px' : '64px', // Collapses down gracefully
            overflow: 'hidden',
            cursor: isExpanded ? 'default' : 'pointer'
          }}
        >
          <Tooltip title={isPlaying ? "Pause Music" : "Play Background Music"}>
            <IconButton 
              onClick={togglePlay} 
              sx={{ 
                width: 44,
                height: 44,
                backgroundColor: isPlaying ? 'rgba(185, 147, 90, 0.1)' : 'transparent',
                animation: !isPlaying ? 'pulseGlow 2.5s infinite' : 'none',
                '&:hover': { backgroundColor: 'rgba(185, 147, 90, 0.2)' }
              }}
            >
              {renderIcon()}
            </IconButton>
          </Tooltip>

          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: isExpanded ? 140 : 0, 
              opacity: isExpanded ? 1 : 0,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <VolumeDownIcon fontSize="small" sx={{ color: '#888', mr: 1 }} />
            <Slider
              aria-label="Volume"
              value={volume}
              min={0}
              max={1}
              step={0.05}
              onChange={handleVolumeChange}
              sx={{ 
                color: '#B9935A',
                height: 4,
                '& .MuiSlider-thumb': { 
                  width: 14, 
                  height: 14,
                  transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                  '&:hover': {
                    boxShadow: '0px 0px 0px 8px rgba(185, 147, 90, 0.16)',
                  }
                },
              }}
            />
            <VolumeUpIcon fontSize="small" sx={{ color: '#B9935A', ml: 1 }} />
          </Box>
        </Paper>
      </ClickAwayListener>
    </>
  );
};

const ThankYouPage = () => (
  <Box sx={{ textAlign: 'center', py: 10 }}>
    <Typography variant="h3">Thank You!</Typography>
    <Typography variant="h6" color="text.secondary">
      Your inquiry has been submitted. We will be in touch shortly.
    </Typography>
  </Box>
);

function AppContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box sx={{ backgroundColor: '#FCF9F3', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />
      <Compass isVisible={!isScrolled} />
      <NaBar />
      <main style={{ flex: 1 }}>
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/experience" element={<Experiences />} />
            <Route path="/destinations" element={<Destination />} />
            <Route path="/destinations/:slug" element={<DestinationDetail />} />
            <Route path="/trips" element={<Trips />} />
            <Route path="/trips/:slug" element={<TripDetail />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:id" element={<BlogDetail />} />
            <Route path="/plan-your-trip" element={<TripPlannerLanding />} />
            <Route path="/plan-your-trip/customize" element={<CustomizeTourWizard />} />
            <Route path="/plan-your-trip/build" element={<BuildTripWizard />} />
            <Route path="/plan-your-trip/consult" element={<ConsultationForm />} />
            <Route path="/plan-your-trip/thank-you" element={<ThankYouPage />} />
          </Routes>
        )}
      </main>
      
      {/* Background Music Player - Fixed Bottom Left */}
      <BackgroundMusicPlayer />
      
      <ScrollActions isVisible={isScrolled} />
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <HelmetProvider>
        <LanguageProvider>
          <CurrencyProvider>
            <AppContent />
          </CurrencyProvider>
        </LanguageProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

export default App;